// 登录页整屏背景：上千条 45° 细线组成的场，线拼出 LN 标志，再依次重组成中台各功能的形状（焦点、收件箱、看板……），最后回到 LN。
// 形状几何来自 lingzhi-design 的标志母版（dist/logo/ln-shapes.json），这里不再抄一份。
// 工程约束：只在尺寸变化时取样一次形状，每帧只画线；页面在后台时停；系统要求减少动效时只画一帧静止的 LN。
import shapesData from '@lingzhi/design-tokens/logo/ln-shapes.json'

interface ShapePart { d: string; rule?: CanvasFillRule }
interface Point { x: number; y: number; diag: number; seed: number; delay: number; a0: number }
interface Mask { inn: Float32Array; glow: Float32Array }

const SHAPES = shapesData.shapes as Record<string, { name: string; parts: ShapePart[] }>
const SEQUENCE: string[] = shapesData.sequence
const BOX_W = shapesData.width
const BOX_H = shapesData.height

const HOLD = 2.6 // 每个形状停多久（秒）
const MORPH = 1.6 // 换形用多久
const INTRO = 1.2 // 开场先把 LN 拼出来
const Q = Math.PI / 4
const BUCKETS = 12

const ease = (x: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, x)), 3)
const sstep = (x: number) => { const k = Math.min(1, Math.max(0, x)); return k * k * (3 - 2 * k) }

export interface LnField {
  start(): void
  stop(): void
  /** 从屏幕上某个点荡出一圈涟漪（打字时用） */
  pulse(clientX: number, clientY: number): void
  destroy(): void
}

export function createLnField(stage: HTMLElement, canvas: HTMLCanvasElement): LnField {
  const ctx = canvas.getContext('2d')
  const root = document.documentElement
  const reduceQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)')
  const reduce = () =>
    root.dataset.lingzhiMotion === 'reduce' || (root.dataset.lingzhiMotion !== 'standard' && !!reduceQuery?.matches)
  const lineColor = () => getComputedStyle(stage).getPropertyValue('--lingzhi-login-stage-fg').trim() || 'currentColor'

  let W = 0, H = 0, unit = 1, ox = 0, oy = 0, gap = 10
  let pts: Point[] = []
  let masks = new Map<string, Mask>()
  const mouse = { x: -9999, y: -9999, k: 0, tk: 0 }
  const pulses: { x: number; y: number; t: number }[] = []
  const paths: number[][] = Array.from({ length: BUCKETS }, () => [])
  let t0 = performance.now()
  let raf = 0
  let running = false
  let paper = '#fff'

  /** 把一个形状画到离屏画布上，再按每条线的位置取样：在形状里多少、在外圈光晕里多少（都是 0–1） */
  function sample(key: string): Mask {
    const cached = masks.get(key)
    if (cached) return cached
    const w = Math.ceil(W), h = Math.ceil(H)
    const off = document.createElement('canvas')
    off.width = w; off.height = h
    const c = off.getContext('2d')!
    c.fillStyle = '#fff'
    c.translate(ox, oy)
    c.scale(unit, unit)
    for (const part of SHAPES[key].parts) c.fill(new Path2D(part.d), part.rule ?? 'nonzero')
    const sharp = c.getImageData(0, 0, w, h).data
    const blur = document.createElement('canvas')
    blur.width = w; blur.height = h
    const b = blur.getContext('2d')!
    b.filter = `blur(${Math.max(4, 5 * unit)}px)`
    b.drawImage(off, 0, 0)
    const soft = b.getImageData(0, 0, w, h).data
    const inn = new Float32Array(pts.length), glow = new Float32Array(pts.length)
    pts.forEach((p, i) => {
      const x = Math.min(w - 1, Math.max(0, Math.round(p.x))), y = Math.min(h - 1, Math.max(0, Math.round(p.y)))
      const k = (y * w + x) * 4 + 3
      inn[i] = sharp[k] / 255
      glow[i] = soft[k] / 255
    })
    const m = { inn, glow }
    masks.set(key, m)
    return m
  }

  function layout(): boolean {
    if (!ctx) return false
    const rect = stage.getBoundingClientRect()
    W = rect.width; H = rect.height
    if (!W || !H) return false
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    paper = lineColor()
    // 宽屏：标志在左侧 60% 的区域里居中，约占屏高三分之一（右边留给登录卡片）；窄屏：标志在上方
    const narrow = W < 860
    const area = narrow ? W : W * 0.6
    const logoH = Math.min(H * (narrow ? 0.16 : 0.33), (area * 0.6) / (BOX_W / BOX_H))
    unit = logoH / BOX_H
    ox = area * 0.5 - (BOX_W / 2) * unit
    oy = H * (narrow ? 0.18 : 0.45) - (BOX_H / 2) * unit
    gap = Math.max(7, Math.round(Math.min(W, H) / (narrow ? 46 : 84)))
    pts = []
    for (let y = gap / 2; y < H + gap; y += gap) {
      for (let x = ((y / gap) % 2) * (gap / 2); x < W + gap; x += gap) {
        // 换形时从左上往右下一路翻过去：按每条线在标志框里的对角位置错开
        const diag = Math.min(1, Math.max(0, (x - ox + (y - oy)) / ((BOX_W + BOX_H) * unit)))
        pts.push({ x, y, diag, seed: Math.random() * 6.283, delay: Math.random() * 0.7, a0: (Math.random() - 0.5) * Math.PI })
      }
    }
    masks = new Map()
    for (const key of SEQUENCE) sample(key)
    return true
  }

  function pulseAt(x: number, y: number, t: number) {
    let v = 0
    for (const p of pulses) {
      const age = t - p.t
      const d = Math.hypot(x - p.x, y - p.y) - age * 900
      v += Math.exp(-(d * d) / (2 * 40 * 40)) * Math.max(0, 1 - age / 1.4)
    }
    return v
  }

  function frame(now: number) {
    if (!ctx) return
    const t = (now - t0) / 1000
    const still = reduce()
    mouse.k += (mouse.tk - mouse.k) * 0.08
    while (pulses.length && t - pulses[0].t > 1.4) pulses.shift()
    ctx.clearRect(0, 0, W, H)
    for (const b of paths) b.length = 0
    const band = (((t / 9) % 1) * 1.6 - 0.3) * (W + H) // 一道光沿 45° 从左上扫到右下

    const cycle = HOLD + MORPH
    const tt = still ? 0 : Math.max(0, t - INTRO)
    const idx = Math.floor(tt / cycle) % SEQUENCE.length
    const phase = (tt % cycle) - HOLD // < 0：停着；0..MORPH：换形
    const A = masks.get(SEQUENCE[idx])!, B = masks.get(SEQUENCE[(idx + 1) % SEQUENCE.length])!
    const morphing = !still && phase > 0

    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]
      const prog = still ? 1 : ease((t - p.delay) / 1.4)
      if (prog <= 0) continue
      let mixB = 0, flip = 0
      if (morphing) {
        mixB = sstep((phase / MORPH - p.diag * 0.45) / 0.55)
        flip = Math.sin(Math.PI * mixB)
      }
      const inn = A.inn[i] + (B.inn[i] - A.inn[i]) * mixB
      const glow = A.glow[i] + (B.glow[i] - A.glow[i]) * mixB
      let v = 0.03 + 0.8 * inn + 0.2 * glow * (1 - inn)
      let ang = Q + flip * Math.PI * 0.25 // 换形时线轻轻翻一下
      if (!still) {
        v += 0.2 * flip * Math.max(inn, glow)
        v += (inn > 0.5 ? 0.5 : 0.1) * Math.exp(-((p.x + p.y - band) ** 2) / (2 * (60 * unit) ** 2))
        v += (inn > 0.5 ? 0.06 : 0.025) * Math.sin(t * 1.3 + p.seed)
        if (pulses.length) v += 0.5 * pulseAt(p.x, p.y, t)
        if (mouse.k > 0.01) {
          const dx = mouse.x - p.x, dy = mouse.y - p.y
          const m = Math.exp(-(dx * dx + dy * dy) / (2 * 110 * 110)) * mouse.k
          v += 0.45 * m
          ang += m * 0.7 * Math.sin(Math.atan2(dy, dx) - Q)
        }
      }
      ang += (1 - prog) * p.a0
      v = Math.max(0, Math.min(1, v * prog))
      if (v < 0.03) continue
      const len = gap * (0.3 + 0.75 * Math.max(inn, v * 0.6)) * (0.4 + 0.6 * prog)
      const hx = (Math.cos(ang) * len) / 2, hy = (Math.sin(ang) * len) / 2
      paths[Math.min(BUCKETS - 1, Math.floor(v * BUCKETS))].push(p.x - hx, p.y - hy, p.x + hx, p.y + hy)
    }

    ctx.strokeStyle = paper
    ctx.lineCap = 'round'
    paths.forEach((arr, i) => {
      if (!arr.length) return
      ctx.globalAlpha = (i + 0.5) / BUCKETS
      ctx.lineWidth = i > 6 ? 1.7 : 1
      ctx.beginPath()
      for (let k = 0; k < arr.length; k += 4) { ctx.moveTo(arr[k], arr[k + 1]); ctx.lineTo(arr[k + 2], arr[k + 3]) }
      ctx.stroke()
    })
    ctx.globalAlpha = 1
    if (still) { running = false; return }
    raf = requestAnimationFrame(frame)
  }

  function start() {
    if (running || document.hidden) return
    if (!layout()) return
    running = true
    raf = requestAnimationFrame(frame)
  }
  function stop() {
    running = false
    cancelAnimationFrame(raf)
  }
  const restart = () => { stop(); start() }
  const onVisibility = () => (document.hidden ? stop() : start())
  const onMove = (e: PointerEvent) => {
    const r = stage.getBoundingClientRect()
    mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; mouse.tk = 1
  }
  const onLeave = () => { mouse.tk = 0 }
  const host = stage.parentElement ?? stage
  const fine = window.matchMedia?.('(pointer: fine)').matches
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(restart) : null
  ro?.observe(stage)
  document.addEventListener('visibilitychange', onVisibility)
  reduceQuery?.addEventListener?.('change', restart)
  if (fine) {
    host.addEventListener('pointermove', onMove)
    host.addEventListener('pointerleave', onLeave)
  }

  return {
    start,
    stop,
    pulse(clientX, clientY) {
      if (reduce() || !running) return
      const r = stage.getBoundingClientRect()
      pulses.push({ x: clientX - r.left, y: clientY - r.top, t: (performance.now() - t0) / 1000 })
      if (pulses.length > 6) pulses.shift()
    },
    destroy() {
      stop()
      ro?.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      reduceQuery?.removeEventListener?.('change', restart)
      host.removeEventListener('pointermove', onMove)
      host.removeEventListener('pointerleave', onLeave)
    },
  }
}

/** 测试与文档用：变形的顺序（中文名） */
export const shapeNames = () => SEQUENCE.map((k) => SHAPES[k].name)
